import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Lock, ArrowRight, CreditCard, FileText, CheckCircle2, User, Building2, ChevronLeft, CalendarClock } from "lucide-react";
import { trackCheckoutBegin, trackPurchase } from "@/lib/analytics";
import { getAffiliateRef } from "@/lib/attribution";
import B2BBookingForm from "@/components/site/B2BBookingForm";
import JahresplanForm from "@/components/site/JahresplanForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  birthdate: "",
  street: "",
  zip_code: "",
  city: "",
  organisation: "",
};

function Field({ id, label, children }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-[12px] font-semibold text-sand-700 uppercase tracking-wider">
        {label}
      </Label>
      {children}
    </div>
  );
}

const inputCls =
  "h-11 rounded-xl bg-cream-50 border-cream-300 text-sand-800 placeholder:text-sand-400 focus-visible:ring-terra";

export default function BookingDialog({ course, open, onOpenChange, initialBuyerType = null }) {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [payMethod, setPayMethod] = useState("online");
  const [consent, setConsent] = useState(false);
  const [invoiceDone, setInvoiceDone] = useState(false);
  const [buyerType, setBuyerType] = useState(initialBuyerType);
  const [promo, setPromo] = useState("");
  const [promoApplied, setPromoApplied] = useState(null); // {code, pct}
  const [promoChecking, setPromoChecking] = useState(false);
  const [raten, setRaten] = useState(6);

  useEffect(() => {
    if (!open || promoApplied) return;
    const ref = getAffiliateRef();
    if (!ref) return;
    axios
      .get(`${API}/discount/${encodeURIComponent(ref)}`)
      .then((res) => {
        setPromo(res.data.code);
        setPromoApplied(res.data);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  if (!course) return null;

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const isPaket = course.id.startsWith("paket-");

  const basePrice = (() => {
    const s = String(course.price).replace(/[^\d.,]/g, "").replace(/\./g, "").replace(",", ".");
    return Number(s) || 0;
  })();
  const finalPrice = promoApplied ? Math.round(basePrice * (1 - promoApplied.pct / 100) * 100) / 100 : basePrice;
  const fmtEur = (v) => v.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const priceLabel = `€ ${fmtEur(finalPrice)}`;
  const monthlyFor = (n) => Math.floor((finalPrice * 100) / n) / 100;

  const applyPromo = async () => {
    if (!promo.trim()) return;
    setPromoChecking(true);
    try {
      const res = await axios.get(`${API}/discount/${encodeURIComponent(promo.trim())}`);
      setPromoApplied(res.data);
      toast.success(`Rabatt-Code eingelöst: −${res.data.pct} %`);
    } catch {
      setPromoApplied(null);
      toast.error("Ungültiger Rabatt-Code.");
    } finally {
      setPromoChecking(false);
    }
  };

  const handleClose = (v) => {
    if (submitting) return;
    if (!v) {
      setInvoiceDone(false);
      setBuyerType(initialBuyerType);
    }
    onOpenChange(v);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const segment = buyerType === "pflegedienst" ? "b2b" : "b2c";
    try {
      if (payMethod === "rechnung") {
        await axios.post(`${API}/bookings/invoice`, {
          course_id: course.id,
          ...form,
          widerruf_consent: consent,
          discount_code: promoApplied?.code || null,
          buyer_type: buyerType || "selbstzahler",
        });
        trackPurchase(finalPrice, `invoice-${course.id}-${Date.now()}`, segment);
        setInvoiceDone(true);
        setSubmitting(false);
        return;
      }
      trackCheckoutBegin({ course_id: course.id, course_title: course.title, amount: finalPrice });
      const endpoint = payMethod === "raten" ? `${API}/checkout/raten` : `${API}/checkout/session`;
      const res = await axios.post(endpoint, {
        course_id: course.id,
        origin_url: window.location.origin,
        ...form,
        widerruf_consent: consent,
        discount_code: promoApplied?.code || null,
        buyer_type: buyerType || "selbstzahler",
        ...(payMethod === "raten" ? { raten } : {}),
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      throw new Error("Keine Checkout-URL erhalten");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Buchung konnte nicht gestartet werden. Bitte prüfe deine Angaben oder rufe uns an: 0211 29273330."
      );
      setSubmitting(false);
    }
  };

  if (invoiceDone) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent data-testid="booking-invoice-success" aria-modal="true" className="max-w-md bg-white border-cream-300 rounded-3xl p-8 text-center">
          <CheckCircle2 className="h-12 w-12 text-sage-dark mx-auto mb-4" strokeWidth={1.4} />
          <DialogTitle className="font-magazine text-2xl font-normal text-sand-800">
            Buchung eingegangen!
          </DialogTitle>
          <DialogDescription className="text-sand-600 text-[14.5px] leading-relaxed mt-2">
            Dein Platz für <strong className="text-sand-800">{course.title}</strong> ist reserviert.
            Du erhältst in den nächsten Tagen deine <strong className="text-sand-800">Rechnung per E-Mail</strong> an{" "}
            {form.email}. Eine Eingangsbestätigung ist bereits unterwegs.
          </DialogDescription>
          <Button onClick={() => handleClose(false)} data-testid="booking-invoice-close"
            className="mt-6 rounded-full bg-terra hover:bg-terra-dark text-white px-8">
            Alles klar
          </Button>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        data-testid="booking-dialog"
        aria-modal="true"
        className="max-w-xl max-h-[92vh] overflow-y-auto bg-white border-cream-300 rounded-3xl p-0"
      >
        <div className="bg-cream-100 border-b border-cream-300 rounded-t-3xl px-7 py-6">
          <DialogHeader className="text-left">
            <div className="text-[10px] uppercase tracking-[0.28em] font-semibold text-terra mb-1.5">
              {isPaket ? "Jahres-Fortbildungsplan" : buyerType === "pflegedienst" ? "Team-Anmeldung für Einrichtungen" : "Kursbuchung"}
            </div>
            <DialogTitle className="font-magazine text-2xl font-normal text-sand-800">
              {course.title}
            </DialogTitle>
            <DialogDescription className="text-sand-600 text-sm">
              {isPaket
                ? <>Festpreis: <span className="font-semibold text-sand-800">{course.price}</span> · 12 Monate · alle Mitarbeitenden</>
                : buyerType === "pflegedienst"
                ? <>Preis pro Teilnehmer:in: <span className="font-semibold text-sand-800">{course.price}</span> · Teamrabatt ab 3 Teilnehmern</>
                : <>Preis: <span className="font-semibold text-sand-800">{course.price}</span> · Zahlung sicher über Stripe</>}
            </DialogDescription>
            {!isPaket && buyerType && initialBuyerType === null && (
              <button type="button" onClick={() => setBuyerType(null)} data-testid="booking-back-to-type"
                className="mt-2 inline-flex items-center gap-1 text-[12px] text-sand-600 hover:text-sand-800 self-start">
                <ChevronLeft className="h-3.5 w-3.5" strokeWidth={2} /> Zurück zur Auswahl
              </button>
            )}
          </DialogHeader>
        </div>

        {/* Jahresplan: Festpreis pro Pflegedienst – eigener Flow, kein Wer-bucht-Schritt */}
        {isPaket && <JahresplanForm course={course} onDone={() => handleClose(false)} />}

        {/* Schritt 0: Wer bucht? */}
        {!isPaket && buyerType === null && (
          <div className="px-7 pb-8 pt-6 space-y-3" data-testid="booking-buyer-type-step">
            <p className="text-[13.5px] text-sand-600 leading-relaxed mb-4">Wer meldet sich an?</p>
            <button type="button" data-testid="booking-type-selbstzahler" onClick={() => setBuyerType("selbstzahler")}
              className="w-full flex items-start gap-4 rounded-2xl border border-cream-300 bg-cream-50 hover:border-terra hover:bg-terra/5 p-5 text-left transition-colors group">
              <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white border border-cream-300 text-terra">
                <User className="h-5 w-5" strokeWidth={1.7} />
              </span>
              <span className="flex-1">
                <span className="block text-[15px] font-semibold text-sand-800">Ich melde mich selbst an</span>
                <span className="block text-[12.5px] text-sand-600 mt-1 leading-snug">
                  Selbstzahler oder mit Bildungsgutschein · Online-Zahlung oder Rechnung
                </span>
              </span>
              <ArrowRight className="h-4 w-4 text-sand-400 group-hover:text-terra group-hover:translate-x-1 transition-all mt-3" strokeWidth={1.8} />
            </button>
            <button type="button" data-testid="booking-type-pflegedienst" onClick={() => setBuyerType("pflegedienst")}
              className="w-full flex items-start gap-4 rounded-2xl border border-cream-300 bg-cream-50 hover:border-terra hover:bg-terra/5 p-5 text-left transition-colors group">
              <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white border border-cream-300 text-terra">
                <Building2 className="h-5 w-5" strokeWidth={1.7} />
              </span>
              <span className="flex-1">
                <span className="block text-[15px] font-semibold text-sand-800">Ich melde Mitarbeiter:innen an</span>
                <span className="block text-[12.5px] text-sand-600 mt-1 leading-snug">
                  Pflegedienst / Einrichtung · mehrere Teilnehmer auf einmal ·{" "}
                  <strong className="text-terra">bis −20 % Teamrabatt</strong>
                </span>
              </span>
              <ArrowRight className="h-4 w-4 text-sand-400 group-hover:text-terra group-hover:translate-x-1 transition-all mt-3" strokeWidth={1.8} />
            </button>
          </div>
        )}

        {/* B2B: Team-Anmeldung */}
        {!isPaket && buyerType === "pflegedienst" && (
          <B2BBookingForm course={course} onDone={() => handleClose(false)} />
        )}

        {/* Selbstzahler: bisheriges Formular */}
        {!isPaket && buyerType === "selbstzahler" && (
        <form onSubmit={handleSubmit} className="px-7 pb-7 pt-5 space-y-4">
          <p className="text-[13px] text-sand-600 leading-relaxed">
            Für deine Anmeldung und das Zertifikat benötigen wir folgende Angaben:
          </p>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field id="bk-first" label="Vorname *">
              <Input id="bk-first" data-testid="booking-first-name" className={inputCls} required minLength={2}
                autoComplete="given-name" placeholder="Maria" value={form.first_name} onChange={set("first_name")} />
            </Field>
            <Field id="bk-last" label="Nachname *">
              <Input id="bk-last" data-testid="booking-last-name" className={inputCls} required minLength={2}
                autoComplete="family-name" placeholder="Mustermann" value={form.last_name} onChange={set("last_name")} />
            </Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field id="bk-email" label="E-Mail-Adresse *">
              <Input id="bk-email" data-testid="booking-email" className={inputCls} type="email" required
                autoComplete="email" placeholder="maria@beispiel.de" value={form.email} onChange={set("email")} />
            </Field>
            <Field id="bk-phone" label="Telefonnummer *">
              <Input id="bk-phone" data-testid="booking-phone" className={inputCls} type="tel" required minLength={5}
                autoComplete="tel" placeholder="0171 1234567" value={form.phone} onChange={set("phone")} />
            </Field>
          </div>

          <Field id="bk-birth" label="Geburtsdatum *">
            <Input id="bk-birth" data-testid="booking-birthdate" className={inputCls} type="date" required
              max={new Date().toISOString().split("T")[0]} value={form.birthdate} onChange={set("birthdate")} />
          </Field>

          <Field id="bk-street" label="Straße &amp; Hausnummer *">
            <Input id="bk-street" data-testid="booking-street" className={inputCls} required minLength={3}
              autoComplete="street-address" placeholder="Musterstraße 12" value={form.street} onChange={set("street")} />
          </Field>

          <div className="grid grid-cols-[120px_1fr] gap-4">
            <Field id="bk-zip" label="PLZ *">
              <Input id="bk-zip" data-testid="booking-zip" className={inputCls} required minLength={4} maxLength={10}
                inputMode="numeric" autoComplete="postal-code" placeholder="40210" value={form.zip_code} onChange={set("zip_code")} />
            </Field>
            <Field id="bk-city" label="Ort *">
              <Input id="bk-city" data-testid="booking-city" className={inputCls} required minLength={2}
                autoComplete="address-level2" placeholder="Düsseldorf" value={form.city} onChange={set("city")} />
            </Field>
          </div>

          {/* Rabatt-Code */}
          <div className="space-y-1.5 pt-1">
            <Label htmlFor="bk-promo" className="text-[12px] font-semibold text-sand-700 uppercase tracking-wider">
              Rabatt-Code (optional)
            </Label>
            {promoApplied ? (
              <div data-testid="booking-promo-applied"
                className="flex items-center justify-between rounded-xl border border-sage bg-sage/10 px-4 py-3">
                <span className="text-[13px] text-sand-800">
                  <strong>{promoApplied.code}</strong> eingelöst: <strong className="text-sage-dark">−{promoApplied.pct} %</strong>{" "}
                  → neuer Preis <strong>{priceLabel}</strong>
                </span>
                <button type="button" data-testid="booking-promo-remove"
                  onClick={() => { setPromoApplied(null); setPromo(""); }}
                  className="text-[12px] text-sand-500 hover:text-sand-800 underline underline-offset-2">
                  Entfernen
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Input id="bk-promo" data-testid="booking-promo-input" className={inputCls}
                  placeholder="Aktionscode eingeben" maxLength={40} value={promo}
                  onChange={(e) => setPromo(e.target.value.toUpperCase())}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyPromo(); } }} />
                <Button type="button" data-testid="booking-promo-apply" onClick={applyPromo}
                  disabled={promoChecking || !promo.trim()}
                  className="h-11 rounded-xl bg-terra hover:bg-terra-dark text-white px-5 shrink-0 disabled:opacity-50">
                  {promoChecking ? <Loader2 className="h-4 w-4 animate-spin" /> : "Einlösen"}
                </Button>
              </div>
            )}
          </div>

          {/* Zahlungsart */}
          <div className="space-y-1.5 pt-1">
            <Label className="text-[12px] font-semibold text-sand-700 uppercase tracking-wider">Zahlungsart *</Label>
            <div className="grid sm:grid-cols-2 gap-3">
              <button
                type="button"
                data-testid="booking-pay-online"
                onClick={() => setPayMethod("online")}
                className={`flex items-start gap-3 rounded-xl border p-4 text-left transition-colors ${
                  payMethod === "online" ? "border-terra bg-terra/5" : "border-cream-300 bg-cream-50 hover:border-sand-400"
                }`}
              >
                <CreditCard className={`h-5 w-5 mt-0.5 shrink-0 ${payMethod === "online" ? "text-terra" : "text-sand-500"}`} strokeWidth={1.7} />
                <span>
                  <span className="block text-[13.5px] font-semibold text-sand-800">Online bezahlen</span>
                  <span className="block text-[12px] text-sand-600 mt-0.5 leading-snug">
                    Karte oder Klarna (auch Rechnung & Ratenkauf über Klarna)
                  </span>
                </span>
              </button>
              <button
                type="button"
                data-testid="booking-pay-invoice"
                onClick={() => setPayMethod("rechnung")}
                className={`flex items-start gap-3 rounded-xl border p-4 text-left transition-colors ${
                  payMethod === "rechnung" ? "border-terra bg-terra/5" : "border-cream-300 bg-cream-50 hover:border-sand-400"
                }`}
              >
                <FileText className={`h-5 w-5 mt-0.5 shrink-0 ${payMethod === "rechnung" ? "text-terra" : "text-sand-500"}`} strokeWidth={1.7} />
                <span>
                  <span className="block text-[13.5px] font-semibold text-sand-800">Auf Rechnung</span>
                  <span className="block text-[12px] text-sand-600 mt-0.5 leading-snug">
                    Rechnung per E-Mail · Zahlung per Überweisung
                  </span>
                </span>
              </button>
              <button
                type="button"
                data-testid="booking-pay-raten"
                onClick={() => setPayMethod("raten")}
                className={`sm:col-span-2 flex items-start gap-3 rounded-xl border p-4 text-left transition-colors ${
                  payMethod === "raten" ? "border-terra bg-terra/5" : "border-cream-300 bg-cream-50 hover:border-sand-400"
                }`}
              >
                <CalendarClock className={`h-5 w-5 mt-0.5 shrink-0 ${payMethod === "raten" ? "text-terra" : "text-sand-500"}`} strokeWidth={1.7} />
                <span className="flex-1">
                  <span className="block text-[13.5px] font-semibold text-sand-800">
                    Ratenzahlung <span className="font-normal text-sand-600">— ohne Aufschlag</span>
                  </span>
                  <span className="block text-[12px] text-sand-600 mt-0.5 leading-snug">
                    In 3, 6 oder 12 Monatsraten · automatische Abbuchung · endet automatisch
                  </span>
                </span>
              </button>
            </div>

            {payMethod === "raten" && (
              <div className="grid grid-cols-3 gap-2 pt-1" translate="no" data-testid="booking-raten-options">
                {[3, 6, 12].map((n) => (
                  <button
                    key={n}
                    type="button"
                    data-testid={`booking-raten-${n}`}
                    onClick={() => setRaten(n)}
                    className={`rounded-xl border p-3 text-center transition-colors ${
                      raten === n ? "border-terra bg-terra/5" : "border-cream-300 bg-cream-50 hover:border-sand-400"
                    }`}
                  >
                    <span className="block text-[15px] font-bold text-sand-800">{n} Raten</span>
                    <span className="block text-[12px] text-terra font-semibold mt-0.5">
                      {n} × {fmtEur(monthlyFor(n))} €
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {payMethod === "rechnung" && (
            <Field id="bk-org" label="Einrichtung / Firma (optional, für die Rechnungsanschrift)">
              <Input id="bk-org" data-testid="booking-organisation" className={inputCls} maxLength={160}
                placeholder="Pflegedienst Sonnenschein GmbH" value={form.organisation} onChange={set("organisation")} />
            </Field>
          )}

          {/* § 356 Abs. 4 BGB – freiwillige Zustimmung zum vorzeitigen Maßnahmebeginn */}
          <div className="space-y-2">
            <label
              htmlFor="bk-widerruf"
              className={`flex items-start gap-3 rounded-xl border p-4 cursor-pointer transition-colors ${
                consent ? "border-sage bg-sage/10" : "border-cream-300 bg-cream-50"
              }`}
            >
              <Checkbox
                id="bk-widerruf"
                data-testid="booking-widerruf-checkbox"
                aria-labelledby="bk-widerruf-text"
                checked={consent}
                onCheckedChange={(v) => setConsent(v === true)}
                className="mt-0.5 border-sand-400 data-[state=checked]:bg-sage-dark data-[state=checked]:border-sage-dark"
              />
              <span id="bk-widerruf-text" className="text-[12px] text-sand-700 leading-relaxed">
                <strong className="text-sand-800">Sofortiger Kursstart (optional):</strong>{" "}
                Ich verlange ausdrücklich und stimme zu, dass mit der Ausführung des Vertrages
                (Beginn der Maßnahme) vor Ablauf der Widerrufsfrist begonnen wird. Mir ist bekannt,
                dass ich durch diese Zustimmung mit Beginn der Ausführung des Vertrages mein{" "}
                <Link to="/agb" target="_blank" className="underline underline-offset-2 hover:text-sand-800">Widerrufsrecht</Link>{" "}
                verliere.
              </span>
            </label>
            {!consent && (
              <p data-testid="booking-widerruf-hint" className="text-[12px] text-sand-600 leading-relaxed px-1">
                Ohne diese Zustimmung kannst du selbstverständlich trotzdem buchen – deine Teilnahme
                beginnt dann frühestens <strong className="text-sand-800">nach Ablauf der 14-tägigen Widerrufsfrist</strong>.
              </p>
            )}
          </div>

          <Button
            type="submit"
            disabled={submitting}
            translate="no"
            data-testid="booking-submit"
            className="w-full rounded-full bg-terra hover:bg-terra-dark text-white h-13 py-3.5 text-base font-semibold group disabled:opacity-50"
          >
            {submitting ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {payMethod === "rechnung" ? "Buchung wird übermittelt …" : "Weiterleitung zu Stripe …"}</>
            ) : (
              <>{payMethod === "rechnung"
                  ? `Zahlungspflichtig buchen (${priceLabel})`
                  : payMethod === "raten"
                  ? `Weiter zur Zahlung (${raten} × ${fmtEur(monthlyFor(raten))} €)`
                  : `Weiter zur Zahlung (${priceLabel})`}
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={1.8} /></>
            )}
          </Button>

          <p className="flex items-start gap-2 text-[11.5px] text-sand-500 leading-relaxed">
            <Lock className="h-3.5 w-3.5 mt-0.5 shrink-0" strokeWidth={1.6} />
            <span>
              Mit deiner Buchung akzeptierst du unsere{" "}
              <Link to="/agb" className="underline underline-offset-2 hover:text-sand-800">AGB</Link> und{" "}
              <Link to="/datenschutz" className="underline underline-offset-2 hover:text-sand-800">Datenschutzerklärung</Link>.
              {payMethod === "online" && " Die Zahlung erfolgt verschlüsselt über Stripe."}
            </span>
          </p>
        </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
